import React, { useState, useEffect, useRef, useCallback } from "react";
import { Helmet } from "react-helmet";
import { Search, Filter, TrendingDown, Plus } from "lucide-react";
import Pagination from "./Pagination";

const API_URL =
  process.env.REACT_APP_SEARCH_API ||
  "https://pnrnzdmabwlldxtrpujo.supabase.co/functions/v1/product-search";

const SkeletonCard = () => (
  <div className="bg-gray-800/60 backdrop-blur-sm border-2 border-gray-700/80 rounded-2xl overflow-hidden animate-pulse">
    <div className="bg-gray-700/80 w-full h-56" />
    <div className="p-6">
      <div className="h-4 bg-gray-700/80 rounded w-20 mb-3" />
      <div className="h-5 bg-gray-700/80 rounded w-full mb-2" />
      <div className="h-5 bg-gray-700/80 rounded w-3/4 mb-4" />
      <div className="h-3 bg-gray-700/80 rounded w-24 mb-6" />
      <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/30">
        <div className="h-8 bg-gray-700/80 rounded w-32" />
      </div>
    </div>
  </div>
);

const LazyImage = ({ src, alt, className, onError }) => {
  const [loaded, setLoaded] = useState(false);
  const [inView, setInView] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" }
    );

    if (imgRef.current) observer.observe(imgRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={imgRef}
      className="w-full h-56 bg-gray-900/80 relative overflow-hidden"
    >
      {!loaded && (
        <div className="absolute inset-0 bg-gray-700/50 animate-pulse" />
      )}
      {inView && (
        <img
          src={
            src || "https://dummyimage.com/400x300/1f2937/9ca3af&text=No+Image"
          }
          alt={alt}
          className={`${className} transition-opacity duration-300 ${
            loaded ? "opacity-100" : "opacity-0"
          }`}
          onLoad={() => setLoaded(true)}
          onError={(e) => {
            e.target.src =
              "https://dummyimage.com/400x300/1f2937/9ca3af&text=No+Image";
            setLoaded(true);
            if (onError) onError(e);
          }}
        />
      )}
    </div>
  );
};

const ProductSearch = ({
  onProductClick,
  initialSearchTerm = "",
  initialCategory = "",
}) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [searchSortOpen, setSearchSortOpen] = useState(false);

  const [availableMagnifications, setAvailableMagnifications] = useState([]);
  const [availableMOAs, setAvailableMOAs] = useState([]);

  const prefetchCache = useRef({});

  const urlParams = new URLSearchParams(window.location.search);
  const urlCategory = urlParams.get("category") || "";
  const effectiveCategory = initialCategory || urlCategory;

  const [draftSearch, setDraftSearch] = useState(initialSearchTerm);

  const [appliedFilters, setAppliedFilters] = useState({
    brands: [],
    name: initialSearchTerm,
    categories: effectiveCategory ? [effectiveCategory] : [],
    inStock: false,
    magnifications: [],
    moas: [],
    priceRange: [0, 5000],
    page: 1,
    limit: 24,
  });

  const [pendingFilters, setPendingFilters] = useState({
    brands: [],
    categories: effectiveCategory ? [effectiveCategory] : [],
    inStock: false,
    magnifications: [],
    moas: [],
    priceRange: [0, 5000],
  });

  const popularBrands = [
    "Aimpoint",
    "Holosun",
    "EOTech",
    "Trijicon",
    "Vortex",
    "Leupold",
    "Primary Arms",
    "Sig Sauer",
    "Burris",
    "Bushnell",
  ];

  const categories = [
    "Red Dot",
    "Holographic",
    "LPVO",
    "Prism Scope",
    "Magnifier",
    "Rifle Scope",
  ];

  useEffect(() => {
    fetchAvailableFilterOptions();
  }, []);

  useEffect(() => {
    searchProducts();
  }, [appliedFilters]);

  const fetchAvailableFilterOptions = async () => {
    try {
      const response = await fetch(`${API_URL}?limit=96&page=1`);
      const data = await response.json();

      if (data.success) {
        const mags = new Set();
        const moaSet = new Set();

        data.products.forEach((p) => {
          if (p.magnification) mags.add(p.magnification);
          if (p.moa !== null && p.moa !== undefined) moaSet.add(p.moa);
        });

        setAvailableMagnifications(Array.from(mags).sort());
        setAvailableMOAs(Array.from(moaSet).sort((a, b) => a - b));
      }
    } catch (err) {
      console.error("Error fetching filter options:", err);
    }
  };

  const searchProducts = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (appliedFilters.brands.length > 0)
        params.append("brand", appliedFilters.brands.join(","));
      if (appliedFilters.name) params.append("name", appliedFilters.name);
      if (appliedFilters.categories.length > 0)
        params.append("category", appliedFilters.categories.join(","));
      if (appliedFilters.magnifications.length > 0)
        params.append("magnification", appliedFilters.magnifications.join(","));
      if (appliedFilters.moas.length > 0)
        params.append("moa", appliedFilters.moas.join(","));
      params.append("minPrice", appliedFilters.priceRange[0]);
      params.append("maxPrice", appliedFilters.priceRange[1]);
      if (appliedFilters.inStock) params.append("inStock", "true");
      params.append("page", appliedFilters.page);
      params.append("limit", appliedFilters.limit);

      const response = await fetch(`${API_URL}?${params}`);
      const data = await response.json();

      if (data.success) {
        setProducts(data.products);
        setPagination(data.pagination);
      } else {
        setError("Failed to load products");
      }
    } catch (err) {
      setError("Error connecting to product database");
      console.error("Search error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleProductHover = useCallback(async (product) => {
    const mpn = product.mpn;
    if (!mpn || prefetchCache.current[mpn]) return;
    prefetchCache.current[mpn] = "pending";
    try {
      const response = await fetch(`${API_URL}?mpn=${mpn}`);
      const data = await response.json();
      if (data.success && data.products?.length > 0) {
        prefetchCache.current[mpn] = data.products[0];
      }
    } catch (err) {
      delete prefetchCache.current[mpn];
    }
  }, []);

  const handleProductClick = useCallback(
    (product) => {
      const cached = prefetchCache.current[product.mpn];
      if (cached && cached !== "pending") {
        onProductClick && onProductClick(cached);
      } else {
        onProductClick && onProductClick(product);
      }
    },
    [onProductClick]
  );

  // 🎯 NEW: Add to compare function
  const handleAddToCompare = (e, product) => {
    e.stopPropagation(); // Prevent card click

    const existingCompare = JSON.parse(
      localStorage.getItem("compareList") || "[]"
    );

    if (existingCompare.find((p) => p.sku === product.sku)) {
      alert("Product already in comparison list");
      return;
    }

    if (existingCompare.length >= 4) {
      alert("Maximum 4 products can be compared. Remove one first.");
      return;
    }

    existingCompare.push(product);
    localStorage.setItem("compareList", JSON.stringify(existingCompare));
    alert(`Added to comparison! (${existingCompare.length}/4)`);
  };

  const applyFilters = () => {
    setAppliedFilters({
      ...pendingFilters,
      name: draftSearch,
      page: 1,
      limit: appliedFilters.limit,
    });
    setSearchSortOpen(false);
  };

  const applyFilterOnly = (overrides) => {
    setAppliedFilters((prev) => ({ ...prev, ...overrides, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setAppliedFilters((prev) => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleLimitChange = (newLimit) => {
    setAppliedFilters((prev) => ({ ...prev, limit: newLimit, page: 1 }));
  };

  const clearFilters = () => {
    setDraftSearch("");
    setPendingFilters({
      brands: [],
      categories: [],
      magnifications: [],
      moas: [],
      inStock: false,
      priceRange: [0, 5000],
    });
    setAppliedFilters({
      brands: [],
      name: "",
      categories: [],
      magnifications: [],
      moas: [],
      inStock: false,
      priceRange: [0, 5000],
      page: 1,
      limit: 24,
    });
  };

  const toggleBrand = (brand) => {
    setPendingFilters((prev) => ({
      ...prev,
      brands: prev.brands.includes(brand)
        ? prev.brands.filter((b) => b !== brand)
        : [...prev.brands, brand],
    }));
  };

  const toggleCategory = (cat) => {
    setPendingFilters((prev) => ({
      ...prev,
      categories: prev.categories.includes(cat)
        ? prev.categories.filter((c) => c !== cat)
        : [...prev.categories, cat],
    }));
  };

  const toggleMagnification = (mag) => {
    setPendingFilters((prev) => ({
      ...prev,
      magnifications: prev.magnifications.includes(mag)
        ? prev.magnifications.filter((m) => m !== mag)
        : [...prev.magnifications, mag],
    }));
  };

  const toggleMOA = (moa) => {
    setPendingFilters((prev) => ({
      ...prev,
      moas: prev.moas.includes(moa)
        ? prev.moas.filter((m) => m !== moa)
        : [...prev.moas, moa],
    }));
  };

  const hasActiveFilters =
    appliedFilters.brands.length > 0 ||
    appliedFilters.name ||
    appliedFilters.categories.length > 0 ||
    appliedFilters.magnifications.length > 0 ||
    appliedFilters.moas.length > 0 ||
    appliedFilters.priceRange[1] < 5000 ||
    appliedFilters.inStock;

  const browseCategoryLabel = initialCategory
    ? initialCategory.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : null;
  const browseTitle = browseCategoryLabel
    ? `${browseCategoryLabel} - Compare Prices | OpticPicker`
    : "Browse Optics - Compare Prices | OpticPicker";
  const browseDescription = browseCategoryLabel
    ? `Compare prices on ${browseCategoryLabel} from top retailers including EuroOptic, Brownells, MidwayUSA, and more.`
    : "Browse 7,000+ rifle scopes, red dots, binoculars, and more. Compare prices across top firearms optics retailers.";
  const browseCanonical = initialCategory
    ? `https://www.opticpicker.com/browse?category=${encodeURIComponent(initialCategory)}`
    : "https://www.opticpicker.com/browse";

  return (
    <div>
      <Helmet>
        <title>{browseTitle}</title>
        <meta name="description" content={browseDescription} />
        <meta property="og:title" content={browseTitle} />
        <meta property="og:description" content={browseDescription} />
        <meta property="og:url" content={browseCanonical} />
        <link rel="canonical" href={browseCanonical} />
      </Helmet>
      <div className="bg-gray-900/60 backdrop-blur-md border-b border-gray-700/50 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex justify-center mb-4">
            <button
              onClick={() => setSearchSortOpen(!searchSortOpen)}
              className="flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-bold rounded-xl transition shadow-lg shadow-blue-600/30"
            >
              <Filter className="w-5 h-5" />
              <span>{searchSortOpen ? "Hide" : "Show"} Search & Filters</span>
              <span className="text-white/80 text-xl">
                {searchSortOpen ? "▲" : "▼"}
              </span>
            </button>
          </div>

          {searchSortOpen && (
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search products by name..."
                  className="w-full pl-12 pr-4 py-3 bg-gray-700/50 border border-gray-600/50 rounded-xl text-white focus:ring-2 focus:ring-blue-500 transition"
                  value={draftSearch}
                  onChange={(e) => setDraftSearch(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") applyFilters();
                  }}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <details className="bg-gray-700/50 border border-gray-600/50 rounded-xl">
                    <summary className="px-4 py-3 cursor-pointer text-white font-medium list-none flex items-center justify-between">
                      <span>
                        Optic Type (
                        {pendingFilters.categories.length > 0
                          ? pendingFilters.categories.length + " selected"
                          : "All"}
                        )
                      </span>
                      <Filter className="w-4 h-4" />
                    </summary>
                    <div
                      className="px-4 pb-3"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {categories.map((cat) => (
                        <label
                          key={cat}
                          className="flex items-center gap-2 py-2 cursor-pointer hover:bg-gray-600/30 px-2 rounded transition"
                        >
                          <input
                            type="checkbox"
                            checked={pendingFilters.categories.includes(cat)}
                            onChange={() => toggleCategory(cat)}
                            className="w-4 h-4 flex-shrink-0"
                          />
                          <span className="text-sm text-white">{cat}</span>
                        </label>
                      ))}
                      {pendingFilters.categories.length > 0 && (
                        <button
                          onClick={() =>
                            setPendingFilters((prev) => ({
                              ...prev,
                              categories: [],
                            }))
                          }
                          className="mt-2 text-xs text-red-400 hover:text-red-300 px-2"
                        >
                          Clear types
                        </button>
                      )}
                    </div>
                  </details>
                </div>

                <div>
                  <details className="bg-gray-700/50 border border-gray-600/50 rounded-xl">
                    <summary className="px-4 py-3 cursor-pointer text-white font-medium list-none flex items-center justify-between">
                      <span>
                        Brand (
                        {pendingFilters.brands.length > 0
                          ? pendingFilters.brands.length + " selected"
                          : "All"}
                        )
                      </span>
                      <Filter className="w-4 h-4" />
                    </summary>
                    <div
                      className="px-4 pb-3 max-h-64 overflow-y-auto"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {popularBrands.map((brand) => (
                        <label
                          key={brand}
                          className="flex items-center gap-2 py-2 cursor-pointer hover:bg-gray-600/30 px-2 rounded transition"
                        >
                          <input
                            type="checkbox"
                            checked={pendingFilters.brands.includes(brand)}
                            onChange={() => toggleBrand(brand)}
                            className="w-4 h-4 flex-shrink-0"
                          />
                          <span className="text-sm text-white">{brand}</span>
                        </label>
                      ))}
                      {pendingFilters.brands.length > 0 && (
                        <button
                          onClick={() =>
                            setPendingFilters((prev) => ({
                              ...prev,
                              brands: [],
                            }))
                          }
                          className="mt-2 text-xs text-red-400 hover:text-red-300 px-2"
                        >
                          Clear brands
                        </button>
                      )}
                    </div>
                  </details>
                </div>

                <div>
                  <details className="bg-gray-700/50 border border-gray-600/50 rounded-xl">
                    <summary className="px-4 py-3 cursor-pointer text-white font-medium list-none flex items-center justify-between">
                      <span>
                        Magnification (
                        {pendingFilters.magnifications.length > 0
                          ? pendingFilters.magnifications.length + " selected"
                          : "All"}
                        )
                      </span>
                      <Filter className="w-4 h-4" />
                    </summary>
                    <div
                      className="px-4 pb-3 max-h-64 overflow-y-auto"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {availableMagnifications.map((mag) => (
                        <label
                          key={mag}
                          className="flex items-center gap-2 py-2 cursor-pointer hover:bg-gray-600/30 px-2 rounded transition"
                        >
                          <input
                            type="checkbox"
                            checked={pendingFilters.magnifications.includes(
                              mag
                            )}
                            onChange={() => toggleMagnification(mag)}
                            className="w-4 h-4 flex-shrink-0"
                          />
                          <span className="text-sm text-white">{mag}</span>
                        </label>
                      ))}
                      {pendingFilters.magnifications.length > 0 && (
                        <button
                          onClick={() =>
                            setPendingFilters((prev) => ({
                              ...prev,
                              magnifications: [],
                            }))
                          }
                          className="mt-2 text-xs text-red-400 hover:text-red-300 px-2"
                        >
                          Clear magnification
                        </button>
                      )}
                    </div>
                  </details>
                </div>

                <div>
                  <details className="bg-gray-700/50 border border-gray-600/50 rounded-xl">
                    <summary className="px-4 py-3 cursor-pointer text-white font-medium list-none flex items-center justify-between">
                      <span>
                        MOA (
                        {pendingFilters.moas.length > 0
                          ? pendingFilters.moas.length + " selected"
                          : "All"}
                        )
                      </span>
                      <Filter className="w-4 h-4" />
                    </summary>
                    <div
                      className="px-4 pb-3 max-h-64 overflow-y-auto"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {availableMOAs.map((moa) => (
                        <label
                          key={moa}
                          className="flex items-center gap-2 py-2 cursor-pointer hover:bg-gray-600/30 px-2 rounded transition"
                        >
                          <input
                            type="checkbox"
                            checked={pendingFilters.moas.includes(moa)}
                            onChange={() => toggleMOA(moa)}
                            className="w-4 h-4 flex-shrink-0"
                          />
                          <span className="text-sm text-white">{moa} MOA</span>
                        </label>
                      ))}
                      {pendingFilters.moas.length > 0 && (
                        <button
                          onClick={() =>
                            setPendingFilters((prev) => ({ ...prev, moas: [] }))
                          }
                          className="mt-2 text-xs text-red-400 hover:text-red-300 px-2"
                        >
                          Clear MOA
                        </button>
                      )}
                    </div>
                  </details>
                </div>
              </div>

              <div className="bg-gray-700/50 border border-gray-600/50 rounded-xl p-4">
                <label className="text-sm text-gray-300 mb-3 block font-medium">
                  Price Range: ${pendingFilters.priceRange[0]} - $
                  {pendingFilters.priceRange[1]}
                </label>
                <input
                  type="range"
                  min="0"
                  max="5000"
                  step="50"
                  value={pendingFilters.priceRange[1]}
                  onChange={(e) =>
                    setPendingFilters((prev) => ({
                      ...prev,
                      priceRange: [0, parseInt(e.target.value)],
                    }))
                  }
                  className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer slider-thumb"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>$0</span>
                  <span>$5000</span>
                </div>
              </div>

              <label className="flex items-center gap-3 p-4 bg-gray-700/50 border border-gray-600/50 rounded-xl cursor-pointer hover:bg-gray-700/70 transition">
                <input
                  type="checkbox"
                  checked={pendingFilters.inStock}
                  onChange={(e) =>
                    setPendingFilters((prev) => ({
                      ...prev,
                      inStock: e.target.checked,
                    }))
                  }
                  className="w-5 h-5"
                />
                <span className="text-white font-medium">
                  Show In-Stock Only
                </span>
              </label>

              <div className="flex justify-center pt-4">
                <button
                  onClick={applyFilters}
                  className="px-8 py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl transition shadow-lg shadow-blue-600/30 text-lg"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold text-white">
                {pagination?.totalResults?.toLocaleString() || 0} Products
              </h2>
              <p className="text-sm text-gray-400 mt-1">
                {appliedFilters.categories.length > 0 &&
                  `Category: ${appliedFilters.categories.join(", ")}`}
                {appliedFilters.brands.length > 0 &&
                  ` • Brand: ${appliedFilters.brands.join(", ")}`}
                {appliedFilters.magnifications.length > 0 &&
                  ` • Mag: ${appliedFilters.magnifications.join(", ")}`}
                {appliedFilters.moas.length > 0 &&
                  ` • MOA: ${appliedFilters.moas.join(", ")}`}
                {appliedFilters.priceRange[1] < 5000 &&
                  ` • Max: $${appliedFilters.priceRange[1]}`}
                {appliedFilters.inStock && " • In Stock Only"}
                {!hasActiveFilters && "All Optics"}
              </p>
            </div>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition text-sm font-medium"
              >
                Clear All Filters
              </button>
            )}
          </div>

          {hasActiveFilters && (
            <div className="flex flex-wrap gap-2 mb-4">
              {appliedFilters.categories.map((cat) => (
                <span
                  key={cat}
                  className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm flex items-center gap-2"
                >
                  {cat}
                  <button
                    onClick={() => {
                      const newCats = appliedFilters.categories.filter(
                        (c) => c !== cat
                      );
                      setPendingFilters((prev) => ({
                        ...prev,
                        categories: newCats,
                      }));
                      applyFilterOnly({ categories: newCats });
                    }}
                    className="hover:text-blue-300"
                  >
                    ×
                  </button>
                </span>
              ))}
              {appliedFilters.brands.map((brand) => (
                <span
                  key={brand}
                  className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm flex items-center gap-2"
                >
                  {brand}
                  <button
                    onClick={() => {
                      const newBrands = appliedFilters.brands.filter(
                        (b) => b !== brand
                      );
                      setPendingFilters((prev) => ({
                        ...prev,
                        brands: newBrands,
                      }));
                      applyFilterOnly({ brands: newBrands });
                    }}
                    className="hover:text-green-300"
                  >
                    ×
                  </button>
                </span>
              ))}
              {appliedFilters.magnifications.map((mag) => (
                <span
                  key={mag}
                  className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-sm flex items-center gap-2"
                >
                  {mag}
                  <button
                    onClick={() => {
                      const newMags = appliedFilters.magnifications.filter(
                        (m) => m !== mag
                      );
                      setPendingFilters((prev) => ({
                        ...prev,
                        magnifications: newMags,
                      }));
                      applyFilterOnly({ magnifications: newMags });
                    }}
                    className="hover:text-purple-300"
                  >
                    ×
                  </button>
                </span>
              ))}
              {appliedFilters.moas.map((moa) => (
                <span
                  key={moa}
                  className="px-3 py-1 bg-pink-500/20 text-pink-400 rounded-full text-sm flex items-center gap-2"
                >
                  {moa} MOA
                  <button
                    onClick={() => {
                      const newMOAs = appliedFilters.moas.filter(
                        (m) => m !== moa
                      );
                      setPendingFilters((prev) => ({ ...prev, moas: newMOAs }));
                      applyFilterOnly({ moas: newMOAs });
                    }}
                    className="hover:text-pink-300"
                  >
                    ×
                  </button>
                </span>
              ))}
              {appliedFilters.priceRange[1] < 5000 && (
                <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-sm flex items-center gap-2">
                  Max: ${appliedFilters.priceRange[1]}
                  <button
                    onClick={() => {
                      setPendingFilters((prev) => ({
                        ...prev,
                        priceRange: [0, 5000],
                      }));
                      applyFilterOnly({ priceRange: [0, 5000] });
                    }}
                    className="hover:text-yellow-300"
                  >
                    ×
                  </button>
                </span>
              )}
              {appliedFilters.name && (
                <span className="px-3 py-1 bg-cyan-500/20 text-cyan-400 rounded-full text-sm flex items-center gap-2">
                  Search: {appliedFilters.name}
                  <button
                    onClick={() => {
                      setDraftSearch("");
                      applyFilterOnly({ name: "" });
                    }}
                    className="hover:text-cyan-300"
                  >
                    ×
                  </button>
                </span>
              )}
              {appliedFilters.inStock && (
                <span className="px-3 py-1 bg-orange-500/20 text-orange-400 rounded-full text-sm flex items-center gap-2">
                  In Stock
                  <button
                    onClick={() => {
                      setPendingFilters((prev) => ({
                        ...prev,
                        inStock: false,
                      }));
                      applyFilterOnly({ inStock: false });
                    }}
                    className="hover:text-orange-300"
                  >
                    ×
                  </button>
                </span>
              )}
            </div>
          )}

          {pagination && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-400">
                Showing {pagination.startIndex} - {pagination.endIndex} of{" "}
                {pagination.totalResults.toLocaleString()}
              </p>
              <select
                value={appliedFilters.limit}
                onChange={(e) => handleLimitChange(parseInt(e.target.value))}
                className="px-3 py-2 bg-gray-700/50 border border-gray-600/50 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500"
              >
                {pagination.validLimits.map((limit) => (
                  <option key={limit} value={limit}>
                    {limit} per page
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {error && (
          <div className="bg-red-900/40 border border-red-700/50 rounded-lg p-6 mb-6">
            <p className="text-red-200">{error}</p>
          </div>
        )}

        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {Array.from({ length: 24 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        )}

        {!loading && !error && products.length === 0 && (
          <div className="text-center py-12 bg-gray-800/40 backdrop-blur-sm rounded-2xl border border-gray-700/50">
            <p className="text-gray-300 text-lg">
              No products found matching your filters.
            </p>
            <button
              onClick={clearFilters}
              className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              Clear Filters
            </button>
          </div>
        )}

        {!loading && !error && products.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {products.map((product) => {
                const discountPercent =
                  product.originalPrice &&
                  product.originalPrice > product.currentPrice
                    ? Math.round(
                        ((product.originalPrice - product.currentPrice) /
                          product.originalPrice) *
                          100
                      )
                    : 0;
                return (
                  <div
                    key={product.sku}
                    className="bg-gray-800/60 backdrop-blur-sm border-2 border-gray-700/80 rounded-2xl overflow-hidden hover:border-blue-500/50 hover:shadow-2xl hover:shadow-blue-500/20 transition-all duration-300 group cursor-pointer transform hover:-translate-y-1"
                    onClick={() => handleProductClick(product)}
                    onMouseEnter={() => handleProductHover(product)}
                  >
                    <div className="relative overflow-hidden bg-gray-900/80">
                      <LazyImage
                        src={product.imageUrl}
                        alt={product.name}
                        className="w-full h-56 object-contain group-hover:scale-105 transition-transform duration-300"
                      />
                      {discountPercent > 0 && (
                        <div className="absolute top-3 right-3">
                          <div className="bg-green-500/95 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-sm font-bold flex items-center gap-1 shadow-lg">
                            <TrendingDown className="w-4 h-4" />
                            {discountPercent}% OFF
                          </div>
                        </div>
                      )}
                      {product.inStock && (
                        <div className="absolute top-3 left-3">
                          <div className="bg-blue-500/95 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg">
                            IN STOCK
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="p-6">
                      <div className="mb-4">
                        <div className="inline-block px-3 py-1 bg-blue-600 text-white text-xs font-bold rounded-full mb-2 shadow-md">
                          {product.manufacturer}
                        </div>
                        <h3 className="text-lg font-bold text-white mb-1 line-clamp-2">
                          {product.name}
                        </h3>
                        <p className="text-sm text-gray-400 mb-2">
                          {product.detectedCategory || product.category}
                        </p>
                        {product.mpn && (
                          <p className="text-xs text-gray-500">
                            MPN: {product.mpn}
                          </p>
                        )}
                        <div className="flex gap-2 mt-1">
                          {product.magnification && (
                            <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded">
                              {product.magnification}
                            </span>
                          )}
                          {product.moa && (
                            <span className="text-xs bg-pink-500/20 text-pink-400 px-2 py-0.5 rounded">
                              {product.moa} MOA
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="mb-3 p-4 bg-gray-900/50 rounded-xl border border-gray-700/30">
                        <div className="flex items-baseline gap-3 mb-2">
                          <span className="text-3xl font-bold text-green-400">
                            ${product.currentPrice.toFixed(2)}
                          </span>
                          {product.originalPrice &&
                            product.originalPrice > product.currentPrice && (
                              <span className="text-sm text-gray-500 line-through">
                                ${product.originalPrice.toFixed(2)}
                              </span>
                            )}
                        </div>
                      </div>

                      {/* 🎯 NEW: Compare Button */}
                      <button
                        onClick={(e) => handleAddToCompare(e, product)}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium transition"
                      >
                        <Plus className="w-4 h-4" />
                        Add to Compare
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            {pagination && pagination.totalPages > 1 && (
              <Pagination
                pagination={pagination}
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default ProductSearch;
